/**
 * Email Color Picker
 * Color picker specifically for email template customization with email-specific color presets
 *
 * @file src/components/email/templates/EmailColorPicker.tsx
 * @created 2025-10-25
 */

import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Palette } from 'lucide-react';

interface EmailColorPickerProps {
  label: string;
  value: string;
  onChange: (color: string) => void;
  colorType?: 'brand' | 'text';
}

// 12 quick selection colors for all pickers
const QUICK_SELECTION_COLORS = [
  // White and Cream
  '#FFFFFF', '#FFFBF2',
  // Blues
  '#CFE4FE', '#2FB5EE', '#3F92FF', '#092DA5',
  // Greens
  '#1EA72E', '#3F7319', '#336633',
  '#015C42', '#2C5C5D', '#234220',
  // Yellow
  '#FEDE5B',
  // Pink and Purple
  '#FFE1D8', '#F47259', '#E21287', '#B400FA', '#591B50',
  // Reds
  '#FF2626', '#EC1C24', '#E1241D', '#DC2626', '#A92226', '#890C17',
  // Oranges
  '#E34E21', '#F19120', '#E9A642',
  // Shades
  '#CAA674', '#C0C0C0', '#000000'
];

// Common colors for brand (primary/secondary)
const BRAND_COMMON_COLORS = [
  { color: '#E1241D', label: 'Red' },
  { color: '#1EA72E', label: 'Green' },
  { color: '#FFFBF2', label: 'Cream' }
];

// Common colors for text
const TEXT_COMMON_COLORS = [
  { color: '#000000', label: 'Black' },
  { color: '#FFFFFF', label: 'White' },
  { color: '#FFFBF2', label: 'Cream' }
];

/**
 * EmailColorPicker - A color input component for email templates with email-specific presets
 */
const EmailColorPicker: React.FC<EmailColorPickerProps> = ({
  label,
  value,
  onChange,
  colorType = 'brand'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const colorInputRef = useRef<HTMLInputElement>(null);

  const handleColorChange = (color: string) => {
    onChange(color);
    setIsOpen(false);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const color = event.target.value;
    // Always update the input value to allow typing
    onChange(color);
  };

  const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const color = event.target.value;
    // Validate and correct the format on blur
    if (!color.startsWith('#')) {
      onChange('#' + color);
    } else if (!color.match(/^#([0-9A-F]{3}){1,2}$/i)) {
      // If invalid hex, revert to previous valid value or default
      if (value.match(/^#([0-9A-F]{3}){1,2}$/i)) {
        // Keep current valid value
        event.target.value = value;
      } else {
        // Set to default black
        onChange('#000000');
      }
    }
  };

  const handlePresetClick = (color: string) => {
    handleColorChange(color);
  };

  const commonColors = colorType === 'text' ? TEXT_COMMON_COLORS : BRAND_COMMON_COLORS;

  return (
    <div className="space-y-2">
      <Label htmlFor={`color-${label}`} className="text-sm font-medium">
        {label}
      </Label>

      <div className="flex gap-2">
        {/* Color Preview and Input */}
        <div className="flex items-center gap-2 flex-1">
          <div
            className="w-8 h-8 rounded-md border-2 border-gray-300 cursor-pointer"
            style={{ backgroundColor: value }}
            onClick={() => colorInputRef.current?.click()}
            title="Click to open colour picker"
          />

          <Input
            ref={colorInputRef}
            type="color"
            value={value}
            onChange={handleInputChange}
            className="w-0 h-0 p-0 border-0 invisible"
          />

          <Input
            type="text"
            value={value}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            className="flex-1 font-mono text-sm"
            placeholder="#000000"
            maxLength={7}
          />
        </div>

        {/* Preset Colors Popover */}
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="px-3"
              title="Choose from preset colours"
            >
              <Palette className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-4">
            <div className="space-y-3">
              <h4 className="font-medium text-sm">Quick Selection</h4>
              <div className="grid grid-cols-6 gap-2">
                {QUICK_SELECTION_COLORS.map((color) => (
                  <button
                    key={color}
                    className={`w-8 h-8 rounded-md border-2 hover:scale-110 transition-transform ${
                      value.toUpperCase() === color ? 'border-gray-900' : 'border-gray-300'
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => handlePresetClick(color)}
                    title={color}
                  />
                ))}
              </div>

              <div className="space-y-2 pt-2 border-t">
                <h5 className="font-medium text-xs text-gray-600">
                  Common {colorType === 'text' ? 'Text' : 'Brand'} Colours
                </h5>
                <div className="grid grid-cols-3 gap-2">
                  {commonColors.map(({ color, label: colorLabel }) => (
                    <button
                      key={color}
                      className="w-full h-6 rounded text-xs font-medium hover:scale-105 transition-transform"
                      style={{
                        backgroundColor: color,
                        color: color === '#FFFFFF' || color === '#FFFBF2' ? '#000000' : '#FFFFFF'
                      }}
                      onClick={() => handlePresetClick(color)}
                    >
                      {colorLabel}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

export default EmailColorPicker;
